import React, { useEffect, useState } from 'react'
import { apiGet, getErrorMessage } from '../../utils/api'

interface AgentLite { id: string | number; name: string; email: string; avatar_url?: string | null }

interface Props {
  isOpen: boolean
  onClose: () => void
  onInvite: (agentId: string) => Promise<void>
  excludeAgentId?: string | number
}

export const InviteWhisperModal: React.FC<Props> = ({ isOpen, onClose, onInvite, excludeAgentId }) => {
  const [agents, setAgents] = useState<AgentLite[]>([])
  const [query, setQuery] = useState('')
  const [inviting, setInviting] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setQuery(''); setDone(null); setError(null)
    apiGet<{ agents: AgentLite[] }>('/agents').then((d) => setAgents(d.agents || [])).catch(() => {})
  }, [isOpen])

  if (!isOpen) return null

  const filtered = agents
    .filter((a) => String(a.id) !== String(excludeAgentId))
    .filter((a) => !query || a.name?.toLowerCase().includes(query.toLowerCase()) || a.email?.toLowerCase().includes(query.toLowerCase()))

  const invite = async (a: AgentLite) => {
    setInviting(String(a.id)); setError(null)
    try {
      await onInvite(String(a.id))
      setDone(a.name)
      setTimeout(onClose, 900)
    } catch (e) {
      setError(getErrorMessage(e, 'Failed to invite'))
    } finally {
      setInviting(null)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in" onClick={onClose}>
      <div className="bg-surface rounded-2xl shadow-xl border border-border w-full max-w-md max-h-[80vh] flex flex-col animate-slide-down" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h3 className="text-lg font-semibold text-txt-primary">Invite to whisper</h3>
            <p className="text-xs text-txt-secondary">They'll be able to see this chat and whisper privately to you.</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg text-txt-tertiary hover:text-txt-primary hover:bg-surface-hover transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="px-6 py-3 border-b border-border">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search teammates…"
            autoFocus
            className="w-full px-3 py-2.5 text-sm rounded-lg bg-surface-secondary border border-border text-txt-primary outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/30"
          />
        </div>
        {error && <p className="px-6 pt-2 text-xs text-red-600">{error}</p>}
        {done && <p className="px-6 pt-2 text-xs text-green-600">Invited {done} ✓</p>}
        <div className="flex-1 overflow-y-auto p-3 space-y-1">
          {filtered.length === 0 ? (
            <p className="text-sm text-txt-tertiary text-center py-6">No teammates found</p>
          ) : filtered.map((a) => (
            <button
              key={a.id}
              onClick={() => invite(a)}
              disabled={inviting === String(a.id)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-surface-hover transition-colors text-left disabled:opacity-50"
            >
              <div className="w-8 h-8 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-600 flex items-center justify-center text-xs font-semibold shrink-0">
                {(a.name || '?').charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-txt-primary truncate">{a.name}</p>
                <p className="text-xs text-txt-tertiary truncate">{a.email}</p>
              </div>
              <span className="text-xs font-semibold text-amber-600 shrink-0">{inviting === String(a.id) ? '…' : 'Invite'}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
